import { HttpBase, type HttpResult } from '@library/Security';
import { CategoryGroupType, OfferTile, type IFilterCategoryDto, type IOfferDto, type IOfferTile, type IOfferTileDto } from '@domain/Marketplace';
import type { ConditionType } from '@domain/Vehicle';
import { useUser } from '@domain/Security/User';
import type { IMarketplaceViewModelDto } from '@app/MyMarketplace/Types/IMarketplaceViewModel';
import type { GpsCoordinates, GpsSearchBoundary } from '@services/GpsCoordinates';
import { OfferListOrderBy } from './OfferListOrderBy';

// type GpsCoordinatesParams = {
//     "gpsCoordinates.latitude": number,
//     "gpsCoordinates.longitude": number
// }

// type SearchBoundaryParams = {
//     "searchBoundary.latitude": number,
//     "searchBoundary.longitude": number,
//     "searchBoundary.distanceKms": number
// }

export class MarketplaceApi extends HttpBase
{

    private getSearchBoundaryParams = ( searchBoundary?: GpsSearchBoundary ) =>
    {
        if( searchBoundary == null ) return {};
        return {
            "searchBoundary.latitude": searchBoundary.latitude,
            "searchBoundary.longitude": searchBoundary.longitude,
            "searchBoundary.distanceKms": searchBoundary.distanceKms
        };
    };
    
    private getGpsCoordinateParams = ( searchBoundary?: GpsCoordinates ) =>
    {
        if( searchBoundary == null ) return {};
        return {
            "gpsCoordinates.latitude": searchBoundary.latitude,
            "gpsCoordinates.longitude": searchBoundary.longitude
        };
    };
        
    private getParams = ( searchBoundary: GpsSearchBoundary, otherParams: object ) =>
    {
        const user = useUser();

        return {
            appConsumerId: user?.appConsumerId,
            appConfigurationId: user?.activeVehicle.appConfigurationId, 
            ...this.getSearchBoundaryParams( searchBoundary ),
            ...otherParams
        };
    };

    /**
     * Gets the Marketplace view model
     * @param appConsumerId
     * @param appVehicleId
     * @returns
     */
    async getViewModelAsync( businessConsumerId: string, vehicleId: string, appConfigurationId: string, searchBoundary: GpsSearchBoundary ): Promise<IMarketplaceViewModelDto|null>
    {
        const result = await this.get( `api/consumer/${ businessConsumerId }/marketplace/${ vehicleId }/${appConfigurationId}`, this.getSearchBoundaryParams( searchBoundary ) );
        if( result.success ) return result.data;
        return null;
    }

    /**
     * Gets a list of categories for the given category group type
     * @param appConsumerId
     * @param appVehicleId
     * @returns
     */
    async getCategories( categoryGroupType: CategoryGroupType, searchBoundary: GpsSearchBoundary, appConfigurationId?: string ): Promise<IFilterCategoryDto[]>
    {
    	const request = { categoryGroupType: categoryGroupType, appConfigurationId: appConfigurationId, ...this.getSearchBoundaryParams( searchBoundary ), };
        const result = await this.get( `api/marketplace/categories`, request );
        if( result.success ) return result.data.categories;
        return [];
    }

    /**
     * Gets all offers that are assigned to the Carbucks App Configuration
     * @returns
     */
    async getAppConfigurationOffers(): Promise<IOfferTile[]>
    {
        const user = useUser();
        const appConfigurationId = user!.activeVehicle.appConfigurationId;
        const result = await this.get( `api/marketplace/carbucksappconfiguration/${appConfigurationId}/offers` );
        if( !result.success ) return [];
        return result.data.offers.map( (o:IOfferTileDto) => new OfferTile( o ) );
    }

    /**
     * Gets a specific offer by its ID
     */
    async getOffer( offerId: string, gpsCoordinates: GpsCoordinates | undefined ): Promise<IOfferDto|null>
    {
        const result = await this.get( `api/marketplace/offers/${ offerId }`, this.getGpsCoordinateParams( gpsCoordinates ) );
        if( !result.success || result.data == null ) return null;
        return result.data;
    };

    /**
     * Gets a list of offers for the category id and/or query
     */
    async getOffersForCategory( searchBoundary: GpsSearchBoundary, categoryGroup: CategoryGroupType, categoryId?: string, query?: string, orderBy: OfferListOrderBy = OfferListOrderBy.NewestFirst, pageNumber: number = 0, pageSize: number = 1000 ): HttpResult<any>
    {
        const params = this.getParams( searchBoundary, query ? { searchQuery: query } : { "categoryId": categoryId, "categoryGroupType": categoryGroup, "orderBy": orderBy, "pageNumber": pageNumber, "pageSize": pageSize } );
        const result = await this.get( 'api/marketplace/offers', params );
        return result;
    };

    /**
     * Gets offers for the given category name/id
     * @param categoryId 
     * @param query 
     * @returns 
     */
    async getOffersForSearchQuery( searchBoundary: GpsSearchBoundary, query: string ): HttpResult<any>
    {
        const params = this.getParams( searchBoundary, { searchQuery: query } );
        const result = await this.get( 'api/marketplace/offers', params );
        return result;
    };
    
    /**
     * Gets offers for the given category name/id
     * @param categoryId
     * @param query
     * @returns
     */
    async getOffersForCategoryName( searchBoundary: GpsSearchBoundary, categoryId?: string, query?: string ): Promise<IOfferTile[]>
    {
        const params = this.getParams( searchBoundary, query ? { searchQuery: query } : { categoryId } );
        const result = await this.get( 'api/marketplace/offers', params );
        if( !result.success ) return [];
        return result.data.offers.map( (o:IOfferTileDto) => new OfferTile( o ) );
    };

    /**
     * Gets a list of offers for the condition type
     */
    async getOffersForCondition( conditionType?: ConditionType, isInPma?: boolean ): Promise<IOfferTile[]>
    {
        const user = useUser();
        const appConfigurationId = user!.activeVehicle.appConfigurationId;
        const params: Record<string,any> = { conditionType: conditionType };
        if( isInPma !== undefined ) params.isInPma = isInPma;
        const result = await this.get( `api/marketplace/carbucksappconfiguration/${appConfigurationId}/offers`, params );
        if( !result.success ) return [];
        return result.data.offers.map( (o:IOfferTileDto) => new OfferTile( o ) );
    };

}

export const useMarketplaceApi = () => new MarketplaceApi();
